import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
  FaArrowLeft, 
  FaCheck, 
  FaLocationArrow, 
  FaSearchLocation,
  FaCarSide, 
  FaClock, 
  FaCalendarAlt,
  FaUsers,
  FaChair,
  FaCar,
  FaAngleDown,
  FaMapMarkerAlt,
  FaRoute
} from 'react-icons/fa';
import { supabase } from '../../../data/supabaseClient';
import { AuthContext } from '../../../Provider';
import { CustomButton8, CustomButton2, CustomButton6, CustomInputBox1 } from '../../../Templates';
import { useToast } from '../../../hooks/ToastContext';
import Spinner from '../../../common/Spinner';
import Header from '../../../../headerL';
import EventRouteCalculator from '../EventRouteCalculator';
import { generateRoutePoints } from '../../../../utils/routeMatchingUtils';
import './EventRideForm.css';

function EventRideForm() {
  const { t } = useTranslation('common');
  const { user } = useContext(AuthContext);
  const { id: eventId } = useParams();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'offer'; // 'offer' or 'request'
  const isOfferMode = mode === 'offer';
  
  const navigate = useNavigate();
  const { addToast } = useToast();
  const mapComponentRef = useRef();
  
  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isCarsLoading, setIsCarsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [event, setEvent] = useState(null);
  const [cars, setCars] = useState([]);
  const [showSeatsMenu, setShowSeatsMenu] = useState(false);
  const [showCarsMenu, setShowCarsMenu] = useState(false);
  
  const [formData, setFormData] = useState({
    // Common fields
    selectedDate: new Date(),
    selectedTime: new Date(),
    address_from: {
      street: '',
      city: '',
      country: ''
    },
    // Pre-filled with event location
    address_to: {
      street: '',
      city: '',
      country: ''
    },
    // Driver-specific fields
    selectedCar: '',
    selectedSeat: 1,
    // Route data
    static_map_url: null,
    distance_km: null,
    route_polyline: null,
    waypoints: [], // Store extra stops but don't send to database
    error: null
  });
  
  // Load event data and user's cars
  useEffect(() => {
    const fetchEventAndCars = async () => {
      try {
        setIsLoading(true);
        
        // Fetch event details
        const { data: eventData, error: eventError } = await supabase
          .from('events')
          .select('*')
          .eq('id', eventId)
          .single();
          
        if (eventError) throw eventError;
        
        if (!eventData) {
          throw new Error(t('errors.event_not_found'));
        }
        
        setEvent(eventData);
        
        // Set the destination to the event location
        setFormData(prev => ({
          ...prev,
          address_to: {
            street: eventData.location,
            city: '',
            country: ''
          }
        }));
        
        // Only fetch cars for driver mode
        if (isOfferMode) {
          setIsCarsLoading(true);
          
          const { data: carData, error: carError } = await supabase
            .from('cars')
            .select('id, brand, model, number, accepts_packages')
            .eq('user_id', user.id);
            
          if (carError) throw carError;
          
          setCars(carData || []);
          setIsCarsLoading(false);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        
        addToast({
          type: 'error',
          title: t('errors.fetch_error'),
          description: err.message
        });
        
        navigate(`/events/${eventId}`);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchEventAndCars();
  }, [eventId, isOfferMode, user.id, navigate, t, addToast]);
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (showSeatsMenu && !e.target.closest('.seats-selector')) {
        setShowSeatsMenu(false);
      }
      if (showCarsMenu && !e.target.closest('.car-selector')) {
        setShowCarsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, [showSeatsMenu, showCarsMenu]);
  
  // Handle back button
  const handleBackClick = () => {
    navigate(`/events/${eventId}`);
  };
  
  // Handle car selection
  const handleCarSelect = (car) => {
    setFormData(prev => ({ 
      ...prev, 
      selectedCar: car
    }));
    setShowCarsMenu(false);
  };
  
  // Handle seat selection
  const handleSeatSelect = (seatNumber) => {
    setFormData(prev => ({ ...prev, selectedSeat: seatNumber }));
    setShowSeatsMenu(false);
  };
  
  // Toggle dropdown states
  const toggleSeatsMenu = (e) => {
    e.stopPropagation();
    setShowSeatsMenu(!showSeatsMenu);
    setShowCarsMenu(false);
  };

  const toggleCarsMenu = (e) => {
    e.stopPropagation();
    setShowCarsMenu(!showCarsMenu);
    setShowSeatsMenu(false);
  };
  
  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ 
      ...prev, 
      [field]: value,
      error: null
    }));
  };
  
  // Handle route calculation results
  const handleRouteCalculated = (routeData) => {
    console.log('Route calculated:', routeData);
    setFormData(prev => ({
      ...prev,
      distance_km: routeData.distance_km,
      static_map_url: routeData.static_map_url,
      route_polyline: routeData.route_polyline,
      address_from: routeData.address_from,
      waypoints: routeData.waypoints || []
    }));
  };

  // Form validation
  const validateForm = () => {
    // Common validation for both modes
    if (!formData.address_from.street) {
      setFormData(prev => ({ 
        ...prev, 
        error: isOfferMode 
          ? t('forms.departure_required') 
          : t('forms.pickup_required')
      }));
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: isOfferMode 
          ? t('forms.departure_required') 
          : t('forms.pickup_required')
      });
      return false;
    }
    
    // Only check car for driver mode
    if (isOfferMode && !formData.selectedCar) {
      setFormData(prev => ({ 
        ...prev, 
        error: t('forms.car_required')
      }));
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: t('forms.car_required')
      });
      return false;
    }
    
    // Check for route calculation for both modes
    if (!formData.route_polyline) {
      setFormData(prev => ({
        ...prev,
        error: t('forms.route_calculation_required')
      }));
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: t('forms.route_calculation_required')
      });
      return false;
    }
    
    return true;
  };
  
  // Format time string for database
  const formatTimeString = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    try {
      // Validate form
      if (!validateForm()) {
        return;
      }
      
      setIsSubmitting(true);
      
      if (isOfferMode) {
        // Create post for driver
        try {
          // First create the post without event_id to avoid schema issues
          const postData = {
            user_id: user.id,
            role: "Driver",
            seats: formData.selectedSeat,
            car: formData.selectedCar,
            travel_date: formData.selectedDate,
            travel_time: formatTimeString(formData.selectedTime),
            travel_type: "One-time",
            map_image_url: formData.static_map_url,
            route_polyline: formData.route_polyline,
            search_radius_km: 10,
            distance_km: formData.distance_km || null,
            address_from_street: formData.address_from.street,
            address_from_city: formData.address_from.city,
            address_from_country: formData.address_from.country,
            address_to_street: formData.address_to.street,
            address_to_city: formData.address_to.city,
            address_to_country: formData.address_to.country,
            status: "active"
            // event_id removed to prevent schema error
          };
          
          // Create the post
          const { data: newPost, error: postError } = await supabase
            .from('posts')
            .insert(postData)
            .select()
            .single();
            
          if (postError) throw postError;
        
          // Generate route points for matching
          if (formData.route_polyline && newPost) {
            try {
              const result = await generateRoutePoints(formData.route_polyline, {
                postId: newPost.id,
                packageId: null,
                intervalKm: 5
              });
              
              if (!result.success) {
                console.error('Error generating route points:', result.error);
                addToast({
                  type: 'warning',
                  title: 'Route Matching',
                  description: 'Route matching may not work properly due to an issue with route data.'
                });
              }
            } catch (routeError) {
              console.error('Exception in route points generation:', routeError);
            }
          }
          
          // Add to event_rides table - REMOVING waypoints from the data
          const eventRideData = {
            event_id: parseInt(eventId),
            post_id: newPost.id,
            driver_id: user.id,
            seats_offered: formData.selectedSeat,
            seats_available: formData.selectedSeat,
            departure_location: formData.address_from.street,
            departure_coordinates: null, // We could add this
            departure_time: new Date(
              formData.selectedDate.getFullYear(),
              formData.selectedDate.getMonth(),
              formData.selectedDate.getDate(),
              formData.selectedTime.getHours(),
              formData.selectedTime.getMinutes()
            ).toISOString()
            // Removed waypoints field as it doesn't exist in the database
          };
          
          const { error: eventRideError } = await supabase
            .from('event_rides')
            .insert(eventRideData);
            
          if (eventRideError) throw eventRideError;
          
          // Update user role in event_attendees
          const { error: updateRoleError } = await supabase
            .from('event_attendees')
            .update({ role: 'driver' })
            .eq('event_id', eventId)
            .eq('user_id', user.id);
            
          if (updateRoleError) {
            console.error('Error updating user role:', updateRoleError);
            // Non-critical error, continue
          }
          
          addToast({
            type: 'success',
            title: t('events.ride_offered'),
            description: t('events.ride_offer_success')
          });
        } catch (formError) {
          throw formError; // Rethrow to be caught by the main try/catch
        }
      } else {
        // Passenger mode - Create ride request and feed post
        try {
          // First create a post for the feed
          const postData = {
            user_id: user.id,
            role: "Passenger",
            travel_date: formData.selectedDate,
            travel_time: formatTimeString(formData.selectedTime),
            travel_type: "One-time",
            map_image_url: formData.static_map_url,
            route_polyline: formData.route_polyline,
            search_radius_km: 10,
            distance_km: formData.distance_km || null,
            address_from_street: formData.address_from.street,
            address_from_city: formData.address_from.city,
            address_from_country: formData.address_from.country,
            address_to_street: formData.address_to.street,
            address_to_city: formData.address_to.city,
            address_to_country: formData.address_to.country,
            status: "active"
          };
          
          // Create the post
          const { data: newPost, error: postError } = await supabase
            .from('posts')
            .insert(postData)
            .select()
            .single();
            
          if (postError) throw postError;
          
          // Generate route points for matching
          if (formData.route_polyline && newPost) {
            try {
              const result = await generateRoutePoints(formData.route_polyline, {
                postId: newPost.id,
                packageId: null,
                intervalKm: 5
              });
              
              if (!result.success) {
                console.error('Error generating route points:', result.error);
              }
            } catch (routeError) {
              console.error('Exception in route points generation:', routeError);
            }
          }
          
          // Get coordinates from the calculation if available
          let pickupCoordinates = null;
          if (formData.route_polyline) {
            // For simplicity, use the first point of the route as pickup coordinates
            // In a real implementation, you might want to extract the exact coordinates
            console.log('Using route polyline for coordinates');
          }
        
          // Create ride request entry with valid status and post_id in notes
          const rideRequestData = {
            event_id: parseInt(eventId),
            passenger_id: user.id,
            pickup_location: formData.address_from.street,
            pickup_coordinates: pickupCoordinates,
            status: 'pending', // Changed from 'active' to 'pending' to comply with constraint
            // Removed post_id field as it doesn't exist in the database
            notes: `Pickup time: ${formatTimeString(formData.selectedTime)} | Post ID: ${newPost.id}`,
            waypoints: formData.waypoints
          };
          
          const { error: requestError } = await supabase
            .from('event_ride_requests')
            .insert(rideRequestData);
            
          if (requestError) throw requestError;
          
          // Update user role in event_attendees
          const { error: updateRoleError } = await supabase
            .from('event_attendees')
            .update({ role: 'passenger' })
            .eq('event_id', eventId)
            .eq('user_id', user.id);
            
          if (updateRoleError) {
            console.error('Error updating user role:', updateRoleError);
            // Non-critical error, continue
          }
          
          addToast({
            type: 'success',
            title: t('events.ride_requested'),
            description: t('events.ride_request_success')
          });
        } catch (formError) {
          throw formError;
        }
      }
      
      // Return to event details page
      navigate(`/events/${eventId}`);
    } catch (error) {
      console.error('Error submitting form:', error);
      
      setFormData(prev => ({ 
        ...prev, 
        error: error.message || t('forms.submission_error')
      }));
      
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: error.message || t('forms.submission_error')
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Show loading spinner while fetching data
  if (isLoading || (isOfferMode && isCarsLoading)) {
    return (
      <>
        <Header />
        <div className="event-ride-loading">
          <Spinner 
            size="large" 
            customText={t('app.loading')}
          />
        </div>
      </>
    );
  }
  
  return (
    <>
      <Header />
      <div className="event-ride-form-container">
        <div className="back-button">
          <CustomButton8 onClick={handleBackClick}>
            <FaArrowLeft />
          </CustomButton8>
        </div>
        
        <h2 className="form-heading">
          {isOfferMode 
            ? t('events.offer_ride_to_event') 
            : t('events.request_ride_to_event')}
        </h2>
        
        {/* Event info banner */}
        <div className="event-info-banner">
          <div className="event-name">
            <h3>{event.title}</h3>
          </div>
          <div className="event-details">
            <div className="event-detail">
              <FaCalendarAlt />
              <span>
                {new Date(event.start_date).toLocaleDateString(undefined, { 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric' 
                })}
              </span>
            </div>
            <div className="event-detail">
              <FaClock />
              <span>
                {new Date(event.start_date).toLocaleTimeString(undefined, { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </span>
            </div>
            <div className="event-detail">
              <FaMapMarkerAlt />
              <span>{event.location}</span>
            </div>
          </div>
        </div>
        
        {formData.error && (
          <div className="error-message">
            <span>{formData.error}</span>
          </div>
        )}
        
        <section className="form-section">
          <h3>
            <FaCalendarAlt style={{ marginRight: '8px' }} />
            {t('forms.leaving_at')}
          </h3>
          <div className="datetime-container">
            <div className="date-picker-wrapper">
              <FaCalendarAlt className="icon" />
              <DatePicker
                selected={formData.selectedDate}
                onChange={date => handleChange('selectedDate', date)}
                dateFormat="dd/MM/yyyy"
                className="date-picker-input"
                minDate={new Date()}
              />
            </div>
            <div className="time-picker-wrapper">
              <FaClock className="icon" />
              <DatePicker
                selected={formData.selectedTime}
                onChange={time => handleChange('selectedTime', time)}
                showTimeSelect
                showTimeSelectOnly
                timeFormat="HH:mm"
                timeIntervals={15}
                timeCaption="Time"
                dateFormat="HH:mm"
                className="time-picker-input"
              />
            </div>
          </div>
        </section>
        
        {isOfferMode ? (
          // Driver specific fields
          <section className="form-section">
            <h3>
              <FaCarSide style={{ marginRight: '8px' }} />
              {t('events.ride_details')}
            </h3>
            
            <div className="ride-options">
              <div className="seats-selector">
                <h4>
                  <FaChair style={{ marginRight: '8px' }} />
                  {t('events.free_seats')}
                </h4>
                <div className="dropdown-control">
                  <button 
                    type="button"
                    className="dropdown-button"
                    onClick={toggleSeatsMenu}
                  >
                    <span>{formData.selectedSeat}</span>
                    <FaAngleDown className="dropdown-arrow" />
                  </button>
                  
                  {showSeatsMenu && (
                    <ul className="dropdown-list seats-list">
                      {[1, 2, 3, 4, 5].map(seatNumber => (
                        <li 
                          key={seatNumber}
                          className={formData.selectedSeat === seatNumber ? 'active' : ''}
                          onClick={() => handleSeatSelect(seatNumber)}
                        >
                          {seatNumber}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              
              <div className="car-selector">
                <h4>
                  <FaCar style={{ marginRight: '8px' }} />
                  {t('events.select_car')}
                </h4>
                <div className="dropdown-control">
                  <button 
                    type="button"
                    className="dropdown-button"
                    onClick={toggleCarsMenu}
                  >
                    <span>{formData.selectedCar || t('forms.select_car')}</span>
                    <FaAngleDown className="dropdown-arrow" />
                  </button>
                  
                  {showCarsMenu && (
                    <ul className="dropdown-list cars-list">
                      {cars.length > 0 ? (
                        cars.map(car => (
                          <li 
                            key={car.id}
                            className={formData.selectedCar === `${car.brand} ${car.number}` ? 'active' : ''}
                            onClick={() => handleCarSelect(`${car.brand} ${car.number}`)}
                          >
                            {car.brand} {car.model} - {car.number}
                          </li>
                        ))
                      ) : (
                        <li className="no-cars">
                          {t('profile.no_cars')}
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              </div>
            </div>
            
            <h4 className="location-label">
              <FaLocationArrow style={{ marginRight: '8px' }} />
              {t('events.route_to_event')}
            </h4>
            
            <div className="map-container2">
              <EventRouteCalculator 
                ref={mapComponentRef}
                eventLocation={event.location}
                onRouteCalculated={handleRouteCalculated}
              />
            </div>
          </section>
        ) : (
          // Passenger specific fields - now with route calculator
          <section className="form-section">
            <h3>
              <FaUsers style={{ marginRight: '8px' }} />
              {t('events.route_to_event')}
            </h3>
            
            <h4 className="location-label">
              <FaLocationArrow style={{ marginRight: '8px' }} />
              {t('events.pickup_and_route')}
            </h4>
            
            <div className="map-container2">
              <EventRouteCalculator 
                ref={mapComponentRef}
                eventLocation={event.location}
                onRouteCalculated={handleRouteCalculated}
              />
            </div>
          </section>
        )}
        
        <div className="submit-button-container">
          <CustomButton2 
            label={
              isSubmitting 
                ? t('app.submitting') 
                : isOfferMode 
                  ? t('events.submit_ride_offer') 
                  : t('events.submit_ride_request')
            } 
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? <Spinner size="small" showText={false} /> : <FaCheck />}
          </CustomButton2>
        </div>
      </div>
    </>
  );
}

export default EventRideForm;